import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { ArrowRight, Search } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { ProductSearch } from "@/components/site/ProductSearch";
import { ProductCard } from "@/components/site/ProductCard";
import { fetchCategories, fetchFeaturedProducts, fallbackImage } from "@/lib/catalog";
import { seo } from "@/lib/seo";

const categoriesQO = queryOptions({ queryKey: ["categories"], queryFn: fetchCategories });
const featuredQO = queryOptions({
  queryKey: ["featured"],
  queryFn: () => fetchFeaturedProducts(6),
});

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      ...seo({
        title: "Wholesale Japanese Tea Supplier | Bulk Matcha & Sencha | QT Foods",
        description:
          "Direct from Japan B2B wholesale tea supplier. Bulk matcha powder for business, wholesale hojicha, sencha, and private label Japanese tea solutions.",
      }),
    ],
  }),
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(categoriesQO),
      context.queryClient.ensureQueryData(featuredQO),
    ]),
  component: Index,
});

function Index() {
  const { data: categories } = useSuspenseQuery(categoriesQO);
  const { data: featured } = useSuspenseQuery(featuredQO);

  return (
    <SiteLayout>
      {/* Hero */}
      <section className="relative isolate overflow-hidden border-b border-border min-h-[80vh] flex flex-col justify-center">
        <img
          src="/assets/images/hero.webp"
          alt="Wholesale Japanese Tea and Bulk Matcha"
          width={1920}
          height={1080}
          className="absolute inset-0 -z-10 h-full w-full object-cover opacity-90"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-background via-background/50 to-transparent" />
        <div className="mx-auto max-w-7xl px-5 py-28 lg:px-8 lg:py-40">
          <p className="text-xs uppercase tracking-[0.25em] text-primary">
            Wholesale · Private Label
          </p>
          <h1 className="mt-5 max-w-3xl font-serif text-5xl leading-[1.05] md:text-7xl">
            Premium Japanese tea, wholesale sourced for your brand.
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-foreground/80 md:text-lg">
            QT Foods partners with growers across Japan to supply matcha, sencha, hojicha and
            teaware to retailers, roasters and hospitality groups worldwide.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/collections"
              className="inline-flex items-center gap-2 rounded-sm bg-primary px-6 py-3 text-sm font-medium tracking-wide text-primary-foreground hover:bg-primary/90"
            >
              Browse catalog <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/wholesale"
              className="inline-flex items-center gap-2 rounded-sm border border-foreground/20 bg-background/60 px-6 py-3 text-sm font-medium tracking-wide hover:border-primary hover:text-primary"
            >
              Wholesale program
            </Link>
          </div>
          <div className="mt-8 max-w-xl">
            <ProductSearch>
              <button
                type="button"
                className="flex w-full cursor-text items-center rounded-sm border border-foreground/20 bg-background/60 px-4 py-3.5 backdrop-blur transition-colors hover:border-primary hover:bg-background/80 shadow-sm"
              >
                <Search className="mr-3 h-5 w-5 text-foreground/70" />
                <span className="flex-1 text-left text-lg text-foreground/70">
                  Search for matcha, sencha, teaware...
                </span>
                <span className="hidden sm:inline-block rounded-sm border border-border px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground bg-background/50">
                  Quick Search
                </span>
              </button>
            </ProductSearch>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Catalog</p>
            <h2 className="mt-2 font-serif text-4xl md:text-5xl">Categories</h2>
          </div>
          <Link to="/collections" className="text-sm tracking-wide text-primary hover:underline">
            View all →
          </Link>
        </div>
        <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6 lg:grid-cols-4">
          {categories.map((c) => (
            <Link
              key={c.id}
              to="/collections/$handle"
              params={{ handle: c.handle }}
              className="group relative overflow-hidden bg-muted"
            >
              <img
                src={c.image_url || fallbackImage(c.handle)}
                alt={c.name}
                width={400}
                height={500}
                loading="lazy"
                className="aspect-[4/5] w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-4">
                <h3 className="font-serif text-2xl text-background">{c.name}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured products */}
      <section className="border-t border-border bg-cream">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Selection</p>
              <h2 className="mt-2 font-serif text-4xl md:text-5xl">Featured products</h2>
            </div>
          </div>
          <div className="mt-10 grid grid-cols-2 gap-6 md:grid-cols-3 md:gap-8 lg:grid-cols-3">
            {featured.map((p: any) => (
              <ProductCard
                key={p.id}
                product={p}
                categoryHandle={p.categories?.handle}
                categoryName={p.categories?.name}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Story teaser */}
      <section className="mx-auto max-w-4xl px-5 py-20 text-center lg:px-8 lg:py-28">
        <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Our story</p>
        <h2 className="mt-4 font-serif text-3xl md:text-4xl leading-snug">
          A trading house built on long relationships with Japanese tea growers — and a quiet
          obsession with quality.
        </h2>
        <Link
          to="/about"
          className="mt-8 inline-flex items-center gap-2 text-sm tracking-wide text-primary hover:underline"
        >
          Read our story <ArrowRight className="h-4 w-4" />
        </Link>
      </section>
    </SiteLayout>
  );
}
